import type { TableBlockData, ChartBlockData, SummaryCardBlockData } from '@reports/domain/value-objects';
import type { TableBlockViewDto, ChartBlockViewDto, SummaryCardBlockViewDto } from '@reports/application/dtos';
import type { ReportChartValue } from '@reports/domain/types';
import type { ColumnDefinition } from '@reports/domain/value-objects/blocks/ColumnConfig';
import { formatDateValue } from '@reports/application/utils';

/**
 * Maps a chart value, formatting the x property if it's a date
 *
 * @param {ReportChartValue} value - Chart value to map
 * @returns {Readonly<{ x: string; y: ReadonlyArray<number> }>} Mapped chart value with formatted date if applicable
 */
const mapChartValue = (value: ReportChartValue): Readonly<{ x: string; y: ReadonlyArray<number> }> => {
  return {
    x: formatDateValue(value.x),
    y: value.y,
  };
};

/**
 * Maps table block data to view DTO, filtering and aligning columns according to blockConfig
 *
 * This mapper ensures that:
 * 1. Only columns defined in blockConfig.columnsConfig are included
 * 2. Columns are in the exact order defined in blockConfig.columnsConfig (Map insertion order)
 * 3. Row data is correctly aligned with the filtered columns
 * 4. Columns that exist in blockConfig but not in API response are skipped
 *
 * @param {TableBlockData} tableBlockData - Raw table data from API
 * @param {ReadonlyMap<string, ColumnDefinition>} columnsConfig - Column configuration from blockConfig
 * @returns {TableBlockViewDto} Filtered and aligned table block view DTO
 *
 * @example
 * // Backend returns: columns: ['col_a', 'col_b', 'col_c'], rows: [['A1', 'B1', 'C1']]
 * // blockConfig defines: ['col_a', 'col_c'] (only 2 columns)
 * // Result: columns: ['col_a', 'col_c'], rows: [['A1', 'C1']]
 */
export const mapTableBlockDataToViewDto = (
  tableBlockData: TableBlockData,
  columnsConfig: ReadonlyMap<string, ColumnDefinition>,
): TableBlockViewDto => {
  // Build a map for O(1) lookup of column indices in API response
  const apiColumnIndexMap = new Map<string, number>();
  tableBlockData.columns.forEach((column, index) => {
    apiColumnIndexMap.set(column.key, index);
  });

  // Filter and reorder columns according to blockConfig
  // Only include columns that exist in BOTH blockConfig AND API response
  const filteredColumns: Array<Readonly<{ key: string; value: string }>> = [];
  const columnIndexMapping: number[] = []; // Maps new index -> original API index

  Array.from(columnsConfig.keys()).forEach((configKey) => {
    const apiColumnIndex = apiColumnIndexMap.get(configKey);

    // Only include if column exists in API response
    if (apiColumnIndex !== undefined) {
      const apiColumn = tableBlockData.columns[apiColumnIndex];
      filteredColumns.push({
        key: apiColumn.key,
        value: apiColumn.value,
      });
      columnIndexMapping.push(apiColumnIndex);
    }
  });

  // Reorder row data according to the new column order
  const filteredRows = tableBlockData.rows.map((row) => {
    return columnIndexMapping.map((originalIndex) => row[originalIndex] ?? '');
  });

  return {
    columns: filteredColumns,
    rows: filteredRows,
    pagination: tableBlockData.pagination,
  };
};

/**
 * Maps chart block data to view DTO, formatting date values in the x property
 *
 * Automatically detects when values.x is a date string and formats it to DD/MM/YYYY format.
 *
 * @param {ChartBlockData} chartBlockData - Chart block data to map
 * @returns {ChartBlockViewDto} Mapped chart block view DTO with formatted dates
 *
 * @example
 * const chartData = {
 *   key: 'sales',
 *   series: ['orders'],
 *   values: [{ x: '2025-01-15', y: [100] }]
 * };
 * mapChartBlockDataToViewDto(chartData);
 * // Returns: { key: 'sales', series: ['orders'], values: [{ x: '15/01/2025', y: [100] }] }
 */
export const mapChartBlockDataToViewDto = (chartBlockData: ChartBlockData): ChartBlockViewDto => {
  return {
    key: chartBlockData.key,
    series: chartBlockData.series,
    values: chartBlockData.values.map(mapChartValue),
  };
};

export const mapSummaryCardBlockDataToViewDto = (
  summaryCardBlockData: SummaryCardBlockData,
): SummaryCardBlockViewDto => {
  return {
    value: summaryCardBlockData.value,
    secondaryValue: summaryCardBlockData.secondaryValue,
  };
};
